/**
 * NewBookTile: the dashed "New book" tile at the end of the shelf. A button by default, a link with href.
 * (cockatrice-ui `src/ui/NewBookTile.jsx`, ported to hyperscript; PLAN.md §0)
 */
import './css/new-book-tile.css';
import { mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import { component, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface NewBookTileProps extends Attributes {
  /** Render as a link to this address instead of a button. */
  readonly href?: string | undefined;
  /** Main label. Default "New book". */
  readonly label?: string | undefined;
  /** Second line. Default "or import DOCX / bundle"; "" hides it. */
  readonly hint?: string | undefined;
  /** Click handler (button form). */
  readonly onClick?: ((e: MouseEvent) => void) | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

export default function NewBookTile(props: NewBookTileProps): Child {
  const [local, rest] = splitProps(props, ['href', 'label', 'hint', 'class']);
  const cls = (): string => `ui-new-book-tile${local.class !== undefined && local.class !== '' ? ` ${local.class}` : ''}`;
  const inner = (): Child => [
    component(Icon, { name: 'plus', size: 'lg' }),
    el('span', { class: 'ui-new-book-tile__label' }, () => (local.label !== undefined && local.label !== '' ? local.label : uiText()('ui.new-book-tile.label'))),
    el('small', { class: 'ui-new-book-tile__hint' }, () => local.hint ?? uiText()('ui.new-book-tile.hint')),
  ];
  return when(
    () => local.href !== undefined && local.href !== '',
    () => el('a', mergeProps({ class: cls, href: () => local.href }, rest), inner()),
    () => el('button', mergeProps({ type: 'button', class: cls }, rest), inner()),
  );
}
